import { DOC_TYPE } from 'definitions/DocType';
import * as React from 'react';
import useSWR from 'swr';
import { DocGrpItem, DocGrpItemId } from './PopDocGrpSett';
import { useDiffLevelCalculation } from './useDiffLevelCalculation';

export type DocGrpDiffInfo = {
  lang: string;
  version: string;
  updateTime: string;
  expose: boolean | null;
  displayTarget: number;
  docId: number;
  docType: DOC_TYPE;
  fileCnt: number;
};

export type DiffLevel = 'Same' | 'Similar' | 'Different';

export type DiffLevels = {
  doc: DiffLevel; // 문서 존재여부로 체크. ( 체크된 문서가 모두 존재하면 Same, 모두 없으면 Different, 일 부 없으면 Similar )
  version: DiffLevel;
  updateTime: DiffLevel;
  expose: DiffLevel;
  displayTarget: DiffLevel;
  fileCnt: DiffLevel;
};

export const useApiDocGrp = (
  docGrpId: number
): {
  diffLevel: DiffLevel;
  diffLevels: DiffLevels;
  items: DocGrpItemId[];
  koKr: DocGrpDiffInfo;
  en: DocGrpDiffInfo;
  enUs: DocGrpDiffInfo;
} => {
  /**
   * 1. 문서 그룹 차이 조회.
   * 2. 문서그룹 아이템목록 조회.
   * 3. 2개의 API가 조회되면, 각 비교항목에 대한 정보를 비교해서, 정리한 데이터를 리턴한다.
   * 예를들어, 문제의 크기를 레벨로 구분하여, 문제레벨 값을 리턴한다.
   * 4. 문서레벨 정의
   *  불일치 Different , 일부불일치 Similar, 완전일치. Same
   *  axios.get<{ docGrpItemId: DocGrpItemId }[]>('/api/member/zdata/docgrpitem/list', {params: {docGrpId: p.docGrpId}}).then(res => {
   */

  const { data: docGrpItems } = useSWR<DocGrpItem[]>(docGrpId && ['/api/member/zdata/docgrpitem/list', { docGrpId }]);
  const { data: diffs } = useSWR<DocGrpDiffInfo[]>(docGrpId && ['/api/member/zdata/docgrpmppng/diffs', { docGrpId }]);

  const { items, koKr, en, enUs } = React.useMemo<{
    items: DocGrpItemId[];
    koKr: DocGrpDiffInfo;
    en: DocGrpDiffInfo;
    enUs: DocGrpDiffInfo;
  }>(() => {
    if (!docGrpItems || !diffs) {
      return {
        items: [],
        koKr: undefined,
        en: undefined,
        enUs: undefined,
      };
    }

    const items = docGrpItems.map((item) => item.docGrpItemId);
    const koKr = diffs.find((item) => item.lang === 'ko-KR');
    const en = diffs.find((item) => item.lang === 'en');
    const enUs = diffs.find((item) => item.lang === 'en-US');

    return {
      items,
      koKr,
      en,
      enUs,
    };
  }, [docGrpItems, diffs]);

  const { diffLevel, diffLevels } = useDiffLevelCalculation(items, diffs);

  return { diffLevel, diffLevels, items, koKr, en, enUs };
};
